/**
 * Generate a campaign proposal PDF using browser print.
 *
 * The HTML body is fetched from the stored `agency_campaign_proposal`
 * document template (managed via the admin dashboard) and rendered
 * through the placeholder substitution engine. Opens a new window with
 * the rendered HTML and triggers the print dialog (Save as PDF).
 * Fully supports Korean and all Unicode characters.
 *
 * The exported signature is preserved so the call sites in
 * `CampaignRegistrationForm.tsx` and `CampaignGridCard.tsx` continue
 * to invoke `generateCampaignProposalPdf(data, t)` without change.
 * The function now returns a `Promise<void>` because the template
 * fetch is async; existing call sites use it as a fire-and-forget
 * statement, so the unawaited promise does not alter their observable
 * behaviour.
 *
 * See `.kiro/specs/document-template-management/design.md` §6.10.
 */

import toast from 'react-hot-toast';
import apolloClient from '../graphql/apollo-client';
import { GET_DOCUMENT_TEMPLATES } from '../graphql/queries';
import { renderTemplate } from './templateRenderer';
import {
  getCachedTemplate,
  setCachedTemplate,
} from './documentTemplateCache';

const DOC_TYPE = 'agency_campaign_proposal' as const;

interface DocumentTemplateRow {
  docType: string;
  content: string;
}

export async function generateCampaignProposalPdf(
  data: any,
  t: (key: string, opts?: any) => string
): Promise<void> {
  // 1) Fetch-or-cache the template.
  let templateContent = getCachedTemplate(DOC_TYPE);
  if (templateContent === undefined) {
    try {
      const result = await apolloClient.query<{
        getDocumentTemplates: DocumentTemplateRow[];
      }>({
        query: GET_DOCUMENT_TEMPLATES,
        fetchPolicy: 'network-only',
      });
      const row = result.data?.getDocumentTemplates?.find(
        (r) => r.docType === DOC_TYPE
      );
      if (!row) {
        toast.error(t('admin:documentTemplates.loadFailed.proposal'));
        return;
      }
      templateContent = row.content;
      setCachedTemplate(DOC_TYPE, templateContent);
    } catch {
      toast.error(t('admin:documentTemplates.loadFailed.proposal'));
      return;
    }
  }

  // 2) Build the Placeholder_Context. `context.campaign` is enriched
  //    with computed fields (e.g. net reward after the 80% payout rate)
  //    so the template can reference them without needing custom logic
  //    in the engine.
  const rewardAmountNet = data?.rewardAmount
    ? Math.round(data.rewardAmount * 0.8)
    : 0;
  const enrichedCampaign = {
    ...data,
    rewardAmountNet,
  };
  const context = {
    campaign: enrichedCampaign,
    match: {},
    influencer: {},
    host: {},
    brand: {
      name: data?.brandName,
    },
  };

  // 3) Render the template against the context. The stored template
  //    includes its own `<!DOCTYPE html><html>...<body>...</body></html>`
  //    scaffolding, so the output is the complete HTML document.
  const html = renderTemplate(templateContent, context);

  // 4) Hand off to the existing print window flow.
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
